import React from 'react';

/* Shared field primitives + styles for FAR Part 12/13/14/15 forms.
 * Each form passes a `get`/`set` bound to its slice of acquisition_data. */

export const fs = {
  section: { background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, marginBottom: 16 } as React.CSSProperties,
  title: { fontSize: 15, fontWeight: 700, color: '#1f2937', margin: '0 0 4px' } as React.CSSProperties,
  sub: { fontSize: 11, color: '#6b7280', margin: '0 0 16px' } as React.CSSProperties,
  label: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 } as React.CSSProperties,
  req: { color: '#dc2626' } as React.CSSProperties,
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' } as React.CSSProperties,
  field: { marginBottom: 14 } as React.CSSProperties,
  help: { fontSize: 10, color: '#9ca3af', margin: '2px 0 0' } as React.CSSProperties,
  th: { textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: '#374151', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' } as React.CSSProperties,
  td: { padding: '4px 6px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' } as React.CSSProperties,
  btnAdd: { padding: '6px 12px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 8 } as React.CSSProperties,
  btnDel: { padding: '2px 8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 11, cursor: 'pointer' } as React.CSSProperties,
};

export type Getter = (k: string, def?: any) => any;
export type Setter = (k: string, v: any) => void;

/* Reusable per-form AI Assist bar. Drafts the given narrative fields into the
 * form's own slice via `set`. Advisory — writes suggestions, user edits/saves. */
export function FarAiAssist({ formName, fields, get, set, contextExtra = '' }: {
  formName: string;
  fields: { key: string; label: string; kind?: 'text' | 'long' | 'list' }[];
  get: Getter; set: Setter; contextExtra?: string;
}) {
  const [loading, setLoading] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<Record<string, any> | null>(null);
  const [err, setErr] = React.useState('');
  const run = async () => {
    setLoading(true); setErr(''); setSuggestions(null);
    try {
      // Lazy import to avoid a circular dependency at module load.
      const { requestAiDraft } = await import('./aiAssist');
      const ctx = contextExtra + '\nCurrent values:\n' + fields.map(f => `${f.label}: ${JSON.stringify(get(f.key, ''))}`).join('\n');
      const out = await requestAiDraft(formName, fields as any, ctx);
      if (Object.keys(out).length === 0) setErr('No suggestions returned. Try again.');
      else setSuggestions(out);
    } catch (e: any) { setErr(e.message || 'AI Assist failed.'); }
    setLoading(false);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <button type="button" onClick={run} disabled={loading} style={{ padding: '6px 12px', background: loading ? '#c4b5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}>
        {loading ? 'Drafting...' : '✨ AI Assist this section'}
      </button>
      {err && <span style={{ marginLeft: 10, fontSize: 12, color: '#991b1b' }}>{err}</span>}
      {suggestions && Object.keys(suggestions).length > 0 && (
        <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: 12, marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6' }}>✨ AI suggestions (advisory)</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => { Object.keys(suggestions).forEach(k => set(k, suggestions[k])); setSuggestions(null); }} style={{ padding: '4px 10px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply all</button>
              <button type="button" onClick={() => setSuggestions(null)} style={{ padding: '4px 10px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Dismiss</button>
            </div>
          </div>
          {fields.filter(f => suggestions[f.key]).map(f => (
            <div key={f.key} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{f.label}</span>
                <button type="button" onClick={() => { set(f.key, suggestions[f.key]); setSuggestions(prev => { if (!prev) return prev; const n = { ...prev }; delete n[f.key]; return n; }); }} style={{ padding: '2px 8px', background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
              </div>
              <div style={{ fontSize: 11, color: '#4b5563', whiteSpace: 'pre-wrap' }}>{Array.isArray(suggestions[f.key]) ? suggestions[f.key].join(', ') : String(suggestions[f.key])}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function makeFields(get: Getter, set: Setter) {
  const Text = (k: string, l: string, o: { req?: boolean; help?: string; placeholder?: string } = {}) => (
    <div style={fs.field} key={k}><label style={fs.label}>{l}{o.req && <span style={fs.req}> *</span>}</label>
      <input style={fs.input} value={get(k) || ''} placeholder={o.placeholder || ''} onChange={e => set(k, e.target.value)} />
      {o.help && <p style={fs.help}>{o.help}</p>}</div>
  );
  const Money = (k: string, l: string, o: { req?: boolean } = {}) => (
    <div style={fs.field} key={k}><label style={fs.label}>{l}{o.req && <span style={fs.req}> *</span>}</label>
      <input style={{ ...fs.input, maxWidth: 240 }} value={get(k) || ''} placeholder="$" onChange={e => set(k, e.target.value)} /></div>
  );
  const Long = (k: string, l: string, o: { req?: boolean; help?: string } = {}) => (
    <div style={fs.field} key={k}><label style={fs.label}>{l}{o.req && <span style={fs.req}> *</span>}</label>
      <textarea style={{ ...fs.input, minHeight: 64, resize: 'vertical' }} value={get(k) || ''} onChange={e => set(k, e.target.value)} />
      {o.help && <p style={fs.help}>{o.help}</p>}</div>
  );
  const Date_ = (k: string, l: string, o: { req?: boolean } = {}) => (
    <div style={fs.field} key={k}><label style={fs.label}>{l}{o.req && <span style={fs.req}> *</span>}</label>
      <input type="date" style={{ ...fs.input, maxWidth: 220 }} value={get(k) || ''} onChange={e => set(k, e.target.value)} /></div>
  );
  const Sel = (k: string, l: string, opts: string[], o: { req?: boolean; help?: string } = {}) => (
    <div style={fs.field} key={k}><label style={fs.label}>{l}{o.req && <span style={fs.req}> *</span>}</label>
      <select style={{ ...fs.input, maxWidth: 460 }} value={get(k) || ''} onChange={e => set(k, e.target.value)}>
        <option value="">Select...</option>{opts.map(x => <option key={x} value={x}>{x}</option>)}
      </select>{o.help && <p style={fs.help}>{o.help}</p>}</div>
  );
  const YN = (k: string, l: string, opts: string[] = ['Yes', 'No'], o: { req?: boolean; help?: string } = {}) => (
    <div style={fs.field} key={k}><label style={fs.label}>{l}{o.req && <span style={fs.req}> *</span>}</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {opts.map(x => <button key={x} type="button" onClick={() => set(k, x)} style={{
          padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
          border: `1px solid ${get(k) === x ? '#7c3aed' : '#d1d5db'}`,
          background: get(k) === x ? '#f5f3ff' : 'white', color: get(k) === x ? '#5b21b6' : '#374151',
        }}>{x}</button>)}
      </div>{o.help && <p style={fs.help}>{o.help}</p>}</div>
  );
  const ListField = (k: string, l: string) => {
    const items: string[] = get(k, []) || [];
    return (
      <div style={fs.field} key={k}><label style={fs.label}>{l}</label>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input style={fs.input} value={it} onChange={e => { const n = [...items]; n[i] = e.target.value; set(k, n); }} />
            <button type="button" style={fs.btnDel} onClick={() => set(k, items.filter((_, x) => x !== i))}>×</button>
          </div>
        ))}
        <button type="button" style={fs.btnAdd} onClick={() => set(k, [...items, ''])}>+ Add</button></div>
    );
  };
  const MultiSelect = (k: string, l: string, opts: string[]) => {
    const sel: string[] = get(k, []) || [];
    const toggle = (o: string) => set(k, sel.includes(o) ? sel.filter(x => x !== o) : [...sel, o]);
    return (
      <div style={fs.field} key={k}><label style={fs.label}>{l}</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {opts.map(o => <button key={o} type="button" onClick={() => toggle(o)} style={{
            padding: '4px 10px', borderRadius: 9999, fontSize: 11, cursor: 'pointer',
            border: `1px solid ${sel.includes(o) ? '#10b981' : '#d1d5db'}`,
            background: sel.includes(o) ? '#d1fae5' : 'white', color: sel.includes(o) ? '#065f46' : '#374151',
          }}>{o}</button>)}
        </div></div>
    );
  };
  const Checklist = (k: string, items: string[]) => {
    const checked: string[] = get(k, []) || [];
    const toggle = (o: string) => set(k, checked.includes(o) ? checked.filter(x => x !== o) : [...checked, o]);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} key={k}>
        {items.map(o => (
          <label key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={checked.includes(o)} onChange={() => toggle(o)} style={{ marginTop: 2 }} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    );
  };
  const TableField = (k: string, l: string, columns: any[]) => {
    const rows: any[] = get(k, []) || [];
    const update = (i: number, ck: string, val: any) => set(k, rows.map((r, x) => x === i ? { ...r, [ck]: val } : r));
    const addRow = () => { const blank: any = {}; columns.forEach(c => { blank[c.key] = ''; }); set(k, [...rows, blank]); };
    return (
      <div style={fs.field} key={k}><label style={fs.label}>{l}</label>
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: columns.length * 120 }}>
            <thead><tr>{columns.map(c => <th key={c.key} style={fs.th}>{c.label}</th>)}<th style={fs.th}></th></tr></thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {columns.map(c => (
                    <td key={c.key} style={fs.td}>
                      {c.type === 'select' ? (
                        <select style={{ ...fs.input, fontSize: 11, padding: '4px 6px', minWidth: 110 }} value={row[c.key] || ''} onChange={e => update(i, c.key, e.target.value)}>
                          <option value="">—</option>{c.options.map((o: string) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : c.type === 'long' ? (
                        <textarea style={{ ...fs.input, fontSize: 11, padding: '4px 6px', minHeight: 32, minWidth: 140 }} value={row[c.key] || ''} onChange={e => update(i, c.key, e.target.value)} />
                      ) : (
                        <input style={{ ...fs.input, fontSize: 11, padding: '4px 6px', minWidth: 110 }} value={row[c.key] || ''} onChange={e => update(i, c.key, e.target.value)} />
                      )}
                    </td>
                  ))}
                  <td style={fs.td}><button type="button" style={fs.btnDel} onClick={() => set(k, rows.filter((_, x) => x !== i))}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" style={fs.btnAdd} onClick={addRow}>+ Add row</button></div>
    );
  };
  return { Text, Money, Long, Date_, Sel, YN, ListField, MultiSelect, Checklist, TableField };
}
